import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { writeFootageDrag } from '../../footage/footageDrag';
import { isUsableFootage, type FootageView } from '../../footage/footageView';
import { footageMessages } from '../../messages/footageMessages';
import { formatTime } from '../../timeline/timelineGeometry';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from '../../MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ClipCardProps {
  view: FootageView;
  /** How many times the cut uses the clip ("In use · 2×"). */
  uses?: number;
  /** Shows the source tag ("Yours", "AI · yours"). */
  showSource?: boolean;
  isOn?: boolean;
  /** A click: the editor adds the clip at the play head. */
  onPick: (view: FootageView) => void;
}

/**
 * One footage card of the panels (design b1, c1): the poster, the length, the label, the source
 * tag and "In use · N×". It drags onto the timeline or the frame; a click adds it at the play head.
 * A clip that is not ready yet shows a shimmer and does not drag.
 */
const ClipCardComponent: React.FC<ClipCardProps> = ({ view, uses = 0, showSource = false, isOn = false, onPick }) => {
  const intl = useIntl();
  const usable = isUsableFootage(view);
  const sourceText =
    view.origin === 'ai'
      ? intl.formatMessage(footageMessages.sourceAi)
      : view.origin === 'songVideo'
        ? intl.formatMessage(footageMessages.sourceSongVideo)
        : intl.formatMessage(footageMessages.sourceYours);
  return (
    <button
      type="button"
      className={clsx(styles.clipCard, isOn && styles.clipCardOn)}
      style={view.posterUrl === null ? undefined : { backgroundImage: `url("${view.posterUrl}")` }}
      draggable={usable}
      disabled={!usable}
      aria-label={intl.formatMessage(footageMessages.addClip, { label: view.label })}
      title={view.label}
      onDragStart={(event) =>
        writeFootageDrag(event.dataTransfer, { source: view.source, durationSec: view.durationSec })
      }
      onClick={() => onPick(view)}
    >
      {!usable && <span className={styles.shimmer} />}
      {showSource && view.origin !== 'bank' && (
        <span className={clsx(styles.clipSource, view.origin === 'ai' ? styles.clipSourceAi : styles.clipSourceMine)}>
          {sourceText}
        </span>
      )}
      {view.durationSec !== null && <span className={styles.clipDuration}>{formatTime(view.durationSec)}</span>}
      {uses > 0 && (
        <span className={styles.clipUsed}>{intl.formatMessage(footageMessages.inUse, { count: uses })}</span>
      )}
      <span className={styles.clipLabel}>
        {usable ? view.label : intl.formatMessage(footageMessages.working, { label: view.label })}
      </span>
    </button>
  );
};

export const ClipCard = TypedMemo(ClipCardComponent);
